import React from "react";

import { stringHelpers } from "infrastructure/helpers";
import { commonMessages } from "infrastructure/localization";
import { IUserRoleAccess } from "ui/shared/models/common";

import { checkAuthorization, createUserRoleAccessData } from "./Authorization.helpers";
import { IUserState, UserStateErrors, getInitialUserState, IAuthorizationData } from "./UserProvider.state";

export interface IAppUserContext {
  user?: IUserState;
  initiateAuthentication?: (remote: boolean) => void;
  completeSignIn?: () => void;
  authorize?: () => void;
  signOut?: () => void;
  setTenant?: (tenantId: string) => void;
  setUserRoleAccessData?: (userRoleAccessData: IUserRoleAccess) => void;
}

export class AppUserContext {
  private readonly postSignInParamName = "returnUri";

  private readonly userContext: IAppUserContext;
  private readonly user: IUserState;

  public constructor(userContext: IAppUserContext) {
    this.userContext = userContext;
    this.user = userContext.user || getInitialUserState();
  }

  public isAuthenticated() {
    return this.user.isAuthenticated;
  }

  public getUser() {
    return this.user;
  }

  public getUserId() {
    return this.user.identity?.id.toLowerCase();
  }

  public getTenantId() {
    return this.user.identity?.tenantId.toLowerCase();
  }

  public setTenantId(tenantId: string) {
    this.userContext.setTenant?.(tenantId);
  }

  public getTenants() {
    return [...this.user.tenants];
  }

  public hasTenantAccess(tenantId: string) {
    return !!this.user.tenants.find(t => stringHelpers.equal(t.tenantId, tenantId));
  }

  public getRoleAccessData(): IUserRoleAccess {
    if (!this.user.userRoleAccessData) {
      const userRoleAccessData = createUserRoleAccessData(this.user.roles, this.user.identity?.tenantId);
      this.userContext.setUserRoleAccessData?.(userRoleAccessData);

      return userRoleAccessData;
    }
    return this.user.userRoleAccessData;
  }

  public isAuthenticating() {
    return this.user.authenticationProcessing;
  }

  public isRemoteAuthenticationRequired() {
    return this.user.remoteAuthenticationRequired === true;
  }

  public isAuthorized(authorizationData?: IAuthorizationData[]) {
    return this.isAuthorizedFor(authorizationData, this.user.identity?.tenantId);
  }

  public isAuthorizedFor(authorizationData?: IAuthorizationData[], tenantId?: string) {
    return !!this.user.isAuthorized && checkAuthorization(this.user.roles, authorizationData || [], tenantId);
  }

  public isAuthorizing() {
    return this.user.authorizationProcessing;
  }

  public isSigningOut() {
    return this.user.signOutProcessing === true;
  }

  public hasError() {
    return this.user.error != null;
  }

  public invalidSession() {
    return this.user.error === UserStateErrors.InvalidSession;
  }

  public getError() {
    if (this.user.error === UserStateErrors.SignInFailed) {
      return commonMessages.error("authenticationFailed");
    } else if (this.user.error === UserStateErrors.AuthorizationFailed) {
      return commonMessages.error("authorizationFailed");
    } else if (this.user.error === UserStateErrors.InvalidSession) {
      return commonMessages.error("invalidSession");
    } else {
      return undefined;
    }
  }

  public initiateAuthentication(remote: boolean, returnUri = "/") {
    if (!this.user.authenticationProcessing) {
      this.userContext.initiateAuthentication?.(remote);
    }

    if (remote) {
      return localStorage.setItem(this.postSignInParamName, returnUri);
    }
  }

  public popPostSignInUri() {
    const returnUriValue = localStorage.getItem(this.postSignInParamName) || "/";
    localStorage.removeItem(this.postSignInParamName);

    return returnUriValue;
  }

  public completeSignIn() {
    if (!this.user.isAuthenticated && !this.user.authenticationProcessing) {
      this.userContext.completeSignIn?.();
    }
  }

  public authorize() {
    if (!this.user.isAuthorized && !this.user.authorizationProcessing) {
      this.userContext.authorize?.();
    }
  }

  public signOut() {
    this.userContext.signOut && this.userContext.signOut();
  }
}

export const UserContext = React.createContext<AppUserContext>(new AppUserContext({}));
